<template>
  <div>
    <!-- Resources Table -->
    <b-card class="resources-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Resources") }}</h5>
          <small class="text-muted">{{
            $t("Manage and review resources")
          }}</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12">
          <b-form-input
            id="search"
            v-model="searchQuery"
            :placeholder="$t('Search') + '...'"
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>

        <!-- Class Filter -->
        <b-col xl="2" lg="2" md="2" sm="2" cols="2">
          <v-select
            ref="fil_class"
            v-model="filterObj.clsID"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="[{ name: 'All Classes', id: 0 }, ...classesOptions]"
            :reduce="(val) => val.id"
            :clearable="false"
            label="name"
            class="filter-control"
            append-to-body
            @input="loadSections"
            :placeholder="$t('Select class')"
          />
        </b-col>

        <!-- Section Filter -->
        <b-col xl="2" lg="2" md="2" sm="2" cols="2">
          <v-select
            ref="fil_sec"
            v-model="filterObj.secIDs"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="[{ section: 'All Sections', id: 0 }, ...filterSections]"
            :reduce="(val) => val.id"
            :clearable="false"
            label="section"
            class="filter-control"
            append-to-body
            :placeholder="$t('Select sections')"
            multiple
            @input="onSectionFilterChange"
          />
        </b-col>

        <!-- Action Buttons -->
        <b-col xl="5" lg="5" md="12" sm="12" cols="12" class="text-right">
          <div class="d-flex gap-2 align-items-center justify-content-end">
            <b-button
              v-if="selectedIDs.length > 0 && rights.delete"
              @click="handleBulkDelete()"
              variant="danger"
              :disabled="bulkDeleting"
              class="action-btn-filter mr-1"
            >
              <b-spinner v-if="bulkDeleting" small class="mr-1" />
              <feather-icon v-else icon="Trash2Icon" size="16" class="mr-1" />
              <span class="align-middle"
                >{{ $t("Delete") }} ({{ selectedIDs.length }})</span
              >
            </b-button>

            <b-button
              v-if="selectedIDs.length > 0 && resourcePublishSetting"
              @click="openPublishModal()"
              variant="success"
              :disabled="bulkPublishing"
              class="action-btn-filter mr-1"
            >
              <b-spinner v-if="bulkPublishing" small class="mr-1" />
              <feather-icon v-else icon="CheckIcon" size="16" class="mr-1" />
              <span class="align-middle"
                >{{ $t("Publish") }} ({{ selectedIDs.length }})</span
              >
            </b-button>

            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              @click="toggleSidebar(true)"
              class="action-btn-filter"
              :disabled="request || dataLoading"
            >
              <b-spinner v-if="request || dataLoading" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              {{ $t("Add Resource") }}
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              :key="'page-size-' + pageSize"
              v-model="pageSize"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalRecords }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRecords"
            :per-page="pageSize"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="resources-table"
            ref="selectableTable"
            :items="resources"
            :fields="fields"
            :busy="dataLoading"
            show-empty
            :responsive="false"
            hover
            small
            :empty-text="tableEmptyText"
            @row-clicked="onRowSelect"
          >
            <template #empty>
              <h3 class="mt-1 mb-1 text-center">
                {{ $t("No records found") }}
              </h3>
            </template>
            <!-- BV wraps this slot in ONE td with colspan = fields.length; use flex rows, not <tr>/<tbody> -->
            <template #table-busy>
              <div class="resource-table-busy-skeleton">
                <div
                  v-for="rowIdx in 5"
                  :key="'busy-sk-' + rowIdx"
                  class="busy-skeleton-row"
                >
                  <div
                    v-for="(field, colIdx) in fields"
                    :key="'busy-sk-' + rowIdx + '-' + colIdx"
                    class="busy-skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </div>
                </div>
              </div>
            </template>

            <template #head()="data">
              <div>
                <span>{{ $t(data.label) }}</span>
              </div>
            </template>

            <template #head(title)="data">
              <div v-if="resourcePublishSetting">
                <b-form-checkbox
                  inline
                  :checked="
                    selectedIDs.length > 0 &&
                    selectedIDs.length == resources.length
                  "
                  :disabled="bulkPublishing"
                  @change="(e) => setSelectAll(e)"
                >
                  <span class="font-small-2">{{ $t(data.label) }}</span>
                </b-form-checkbox>
              </div>
              <div v-else>
                <span>{{ $t(data.label) }}</span>
              </div>
            </template>

            <template #head(actions)="data">
              <div class="mr-5 text-right">
                <span>{{ $t(data.label) }}</span>
              </div>
            </template>

            <!-- <template #cell(sno)="data">
            {{ data.index + 1 }}
          </template> -->

            <template #cell(title)="data">
              <div class="title-cell-content">
                <div
                  v-if="resourcePublishSetting"
                  class="title-checkbox-wrapper"
                >
                  <b-form-checkbox
                    inline
                    :checked="selectedIDs.includes(data.item.id)"
                    :disabled="bulkPublishing"
                    @change="(e) => setSelected(e, data.item.id)"
                  >
                    <span class="title-text">{{
                      data.item.title || $t("N/A")
                    }}</span>
                  </b-form-checkbox>
                </div>
                <div v-else class="title-text-wrapper">
                  <span class="title-text">{{
                    data.item.title || $t("N/A")
                  }}</span>
                </div>
                <div class="title-badges">
                  <b-badge
                    v-if="data.item.isPublished"
                    variant="light-success"
                    class="mt-1"
                  >
                    {{ $t("Published") }}
                  </b-badge>
                  <b-badge v-else variant="light-danger" class="mt-1">
                    {{ $t("Unpublished") }}
                  </b-badge>
                </div>
              </div>
            </template>

            <template #cell(class)="data">
              <b-badge variant="light-primary">
                {{ getClassName(data.item.cID) }}
              </b-badge>
            </template>

            <template #cell(section)="data">
              <b-badge variant="light-primary">
                {{ getSecName(data.item.cID, data.item.section) }}
              </b-badge>
            </template>

            <template #cell(subject)="data">
              <b-badge variant="light-primary">
                {{ getSubName(data.item.subID) }}
              </b-badge>
            </template>

            <template #cell(attachments)="data">
              <b-badge
                v-if="data.item.attachments && data.item.attachments.length > 0"
                variant="light-warning"
                class="cursor-pointer"
                @click.stop="rights.edit ? editResource(data.item) : null"
              >
                {{ data.item.attachments.length }} {{ $t("Attachments") }}
              </b-badge>
              <span v-else class="text-muted">{{ $t("No attachments") }}</span>
            </template>

            <template #cell(date)="data">
              <span class="text-nowrap small text-dark font-weight-600">{{
                formatResourceDate(data.item)
              }}</span>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center justify-content-end">
                <b-button
                  v-if="rights.view"
                  variant="gradient-primary"
                  v-b-tooltip.hover.left
                  :title="$t('View Details')"
                  size="sm"
                  class="action-btn-table mr-1"
                  @click.stop="viewResource(item)"
                  :disabled="
                    viewLoading === item.id ||
                    (editLoading && currentID === item.id) ||
                    deleteLoading === item.id
                  "
                >
                  <b-spinner v-if="viewLoading === item.id" small type="grow" />
                  <feather-icon v-else icon="EyeIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.edit"
                  variant="outline-success"
                  v-b-tooltip.hover.top
                  :title="$t('Edit')"
                  size="sm"
                  class="action-btn-outline edit-btn mr-1"
                  @click.stop="editResource(item)"
                  :disabled="
                    viewLoading === item.id ||
                    (editLoading && currentID === item.id) ||
                    deleteLoading === item.id
                  "
                >
                  <b-spinner
                    v-if="editLoading && currentID === item.id"
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="EditIcon" size="16" />
                </b-button>

                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  v-b-tooltip.hover.right
                  :title="$t('Delete')"
                  size="sm"
                  class="action-btn-outline delete-btn"
                  @click.stop="handleDelete(item)"
                  :disabled="
                    viewLoading === item.id ||
                    (editLoading && currentID === item.id) ||
                    deleteLoading === item.id
                  "
                >
                  <b-spinner
                    v-if="deleteLoading === item.id"
                    small
                    type="grow"
                  />
                  <feather-icon v-else icon="Trash2Icon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- Add/Edit Resource Sidebar -->
    <b-sidebar
      id="add-resource-sidebar"
      :visible="isSidebarOpen"
      bg-variant="white"
      backdrop
      shadow
      width="450px"
      no-header-close
      right
      @hidden="onSidebarHidden"
      @change="onSidebarChange"
      class="resource-sidebar"
    >
      <div class="sidebar-header-modern">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <feather-icon
              :icon="
                sidebarMode === 'add'
                  ? 'PlusIcon'
                  : sidebarMode === 'edit'
                  ? 'EditIcon'
                  : 'EyeIcon'
              "
              size="20"
              class="sidebar-title-icon"
            />
            <h5 class="mb-0 sidebar-title">{{ $t(sidebarTitle) }}</h5>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="sidebar-close-btn"
            @click="toggleSidebar(false)"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <b-form @submit.prevent class="sidebar-form-modern">
          <!-- Title -->
          <b-form-group :label="$t('Title')" class="modern-form-group">
            <b-form-input
              v-if="sidebarMode !== 'view'"
              v-model="newResource.title"
              :placeholder="$t('Enter resource title')"
              :state="newResource.title ? true : null"
              class="modern-input"
              trim
            />
            <div v-else class="modern-display-text">
              {{ newResource.title || $t("No title") }}
            </div>
          </b-form-group>

          <!-- Class Selection -->
          <b-form-group :label="$t('Class')" class="modern-form-group">
            <v-select
              v-if="sidebarMode === 'add'"
              v-model="myObj.clsID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="classesOptions"
              :clearable="false"
              label="name"
              :reduce="(val) => val.id"
              :placeholder="$t('Select class')"
              :loading="isClassLoading"
              :state="myObj.clsID ? true : null"
              class="modern-select"
              @input="FillSections"
            />
            <v-select
              v-else-if="sidebarMode === 'edit'"
              v-model="myObj.clsID"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="classesOptions"
              :clearable="false"
              label="name"
              :reduce="(val) => val.id"
             :placeholder="$t('Select class')"
              :loading="isClassLoading"
              :disabled="true"
              class="modern-select"
            />
            <div v-else class="modern-display-text">
              {{ getClassName(myObj.clsID) }}
            </div>
          </b-form-group>

          <!-- Section Selection -->
          <b-form-group :label="$t('Section')" class="modern-form-group">
            <v-select
              v-if="sidebarMode === 'add'"
              v-model="myObj.sec"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="[
                { section: 'All Sections', id: 0 },
                ...currentSections,
              ]"
              :reduce="(val) => val.id"
              label="section"
              :clearable="false"
              :placeholder="$t('Select section')"
              :disabled="!myObj.clsID"
              :loading="isSectionLoading"
              :state="myObj.sec && myObj.sec.length > 0 ? true : null"
              class="modern-select"
              @input="handleSectionSelection"
              multiple
            />
            <v-select
              v-else-if="sidebarMode === 'edit'"
              v-model="myObj.sec"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="currentSections"
              :reduce="(val) => val.id"
              label="section"
              :clearable="false"
              :placeholder="$t('Select section')"
              :loading="isSectionLoading"
              :disabled="true"
              class="modern-select"
            />
            <div v-else class="modern-display-text">
              {{ getSectionDisplayName() }}
            </div>
          </b-form-group>

          <!-- Subject Selection -->
          <b-form-group :label="$t('Subject')" class="modern-form-group">
            <v-select
              v-if="sidebarMode !== 'view'"
              v-model="selectedSubject"
              :key="'subject-' + currentResourceId + '-' + sidebarMode"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="subjects"
              :reduce="(val) => val.id"
              label="subject"
              :clearable="false"
              :placeholder="$t('Select subject')"
              :loading="isSubjectLoading"
              :state="selectedSubject ? true : null"
              class="modern-select"
            />
            <div v-else class="modern-display-text">
              {{ getSubName(selectedSubject) }}
            </div>
          </b-form-group>

          <!-- Add Links Section -->
          <b-form-group
            v-if="sidebarMode !== 'view'"
            :label="$t('Links')"
            class="modern-form-group"
          >
            <!-- Add Link Button -->
            <b-button
              v-if="!showLinkInputs"
              @click="showLinkInputs = true"
              variant="outline-primary"
              size="sm"
              block
              class="modern-btn-outline"
            >
              <feather-icon icon="PlusIcon" class="mr-1" size="14" />
              <span>{{ $t("Add Link") }}</span>
            </b-button>

            <!-- Link Input Fields -->
            <div v-if="showLinkInputs">
              <div
                v-for="(link, index) in linkInputs"
                :key="'link-' + index"
                class="d-flex mb-2 align-items-center"
              >
                <b-form-input
                  v-model="link.url"
                  :placeholder="
                    $t('Enter link URL (e.g., https://example.com)')
                  "
                  type="url"
                  class="mr-2 modern-input"
                  @blur="validateAndAddLink(index)"
                  :state="link.isValid"
                />
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="modern-btn-icon"
                  @click="removeLinkInput(index)"
                >
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </div>

              <b-button
                @click="addLinkInput"
                variant="outline-secondary"
                size="sm"
                block
                class="modern-btn-outline"
              >
                <feather-icon icon="PlusIcon" class="mr-1" size="12" />
                <span>{{ $t("Add Another Link") }}</span>
              </b-button>
            </div>
          </b-form-group>

          <!-- File Upload -->
          <b-form-group
            :label="$t('Attachments')"
            class="modern-form-group"
            v-if="sidebarMode !== 'view'"
          >
            <b-button
              @click="$refs.mediaDoc.click()"
              variant="gradient-primary"
              :disabled="request || mydocloading"
              block
              class="modern-btn-primary"
            >
              <b-spinner
                v-if="mydocloading"
                type="grow"
                small
                :label="$t('Loading...')"
              />
              <template v-else>
                <feather-icon icon="UploadIcon" size="16" class="mr-1" />
                <span>{{ $t("Upload Attachments (Max 25MB)") }}</span>
              </template>
            </b-button>
            <input
              type="file"
              id="mediaDoc"
              hidden
              ref="mediaDoc"
              multiple
              accept="image/*,video/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
              @change="addMedia()"
            />
          </b-form-group>

          <!-- Selected Files Display -->
          <b-col md="12" class="d-flex flex-wrap">
            <div
              class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
              style="width: 80px; height: 80px; position: relative"
              v-for="(file, index) in attachments.filter(
                (el) => el.type !== 'Link'
              )"
              :key="index"
              @click="openFile(file.url)"
            >
              <b-img
                v-if="file.type.toLowerCase() === 'image'"
                :src="getMediaPreviewUrl(file.url, file.type)"
                alt=""
                referrerpolicy="no-referrer"
                class="w-100 h-100 rounded"
                style="object-fit: cover"
                @error="onMediaPreviewError($event, file)"
              />
              <video
                v-else-if="file.type.toLowerCase() === 'video'"
                :src="file.url"
                class="w-100 h-100 rounded"
                preload="metadata"
                style="object-fit: contain"
              />
              <feather-icon
                v-else
                :icon="getFileIcon(file.type)"
                size="30"
                class="text-primary"
              />
              <b-avatar
                v-if="sidebarMode !== 'view'"
                size="20"
                variant="primary"
                style="position: absolute; top: -5px; right: -5px"
                button
                @click.stop="removeMedia(index)"
              >
                <feather-icon icon="XIcon" />
              </b-avatar>
            </div>
          </b-col>

          <!-- Links Display -->
          <b-col
            md="12"
            v-if="
              attachments.filter((el) => el.type === 'Link').length > 0 &&
              sidebarMode === 'view'
            "
          >
            <small class="text-muted d-block mb-2">{{
              $t("Added Links:")
            }}</small>
            <div class="d-flex flex-wrap">
              <div
                v-for="(link, index) in attachments.filter(
                  (el) => el.type === 'Link'
                )"
                :key="'display-link-' + index"
                class="d-flex align-items-center p-2 border rounded mr-2 mb-2"
                style="max-width: 250px"
              >
                <feather-icon
                  icon="LinkIcon"
                  size="16"
                  class="text-primary mr-2"
                />
                <a
                  :href="link.url"
                  target="_blank"
                  class="text-primary text-truncate mr-2"
                  style="max-width: 150px"
                  :title="link.url"
                >
                  {{ link.name }}
                </a>
                <b-button
                  v-if="sidebarMode !== 'view'"
                  variant="outline-danger"
                  size="sm"
                  @click="removeLinkFromAttachments(index)"
                >
                  <feather-icon icon="XIcon" size="12" />
                </b-button>
              </div>
            </div>
          </b-col>

          <!-- Submit Button -->
          <div class="sidebar-footer-modern" v-if="sidebarMode !== 'view'">
            <b-button
              type="submit"
              variant="gradient-primary"
              @click="handleUpload"
              :disabled="request"
              block
              class="modern-btn-submit"
            >
              <b-spinner v-if="request" small type="grow" class="mr-1" />
              <template v-else>
                <feather-icon icon="SaveIcon" size="16" class="mr-1" />
                <span>{{ $t(sidebarButton) }}</span>
              </template>
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Publish Modal -->
    <b-modal
      v-model="publishModal"
      :title="$t('Publish Resources')"
      size="md"
      hide-footer
      centered
    >
      <b-form @submit.prevent="publishSelected">
        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('Select Date Range')"
              :invalid-feedback="$t('Date range is required')"
              ref="dateRange"
            >
              <flat-pickr
                v-model="publishDateRange"
                :config="dateRangeConfig"
                class="form-control"
                :placeholder="$t('Select date range for publishing')"
              />
            </b-form-group>
          </b-col>
          <b-col md="12" class="text-right">
            <b-button
              variant="secondary"
              class="mr-2"
              @click="publishModal = false"
              :disabled="bulkPublishing"
            >
              {{ $t("Cancel") }}
            </b-button>
            <b-button
              variant="success"
              type="submit"
              :disabled="bulkPublishing || !publishDateRange"
            >
              <b-spinner v-if="bulkPublishing" small variant="light" />
              <span v-else>{{ $t("Publish Resources") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { required, minLength, between } from "vuelidate/lib/validators";
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BSpinner,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroup,
  BFormTextarea,
  BAvatar,
  BModal,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import {
  buildUploadedAttachment,
  getMediaPreviewUrl as resolveMediaPreviewUrl,
  getUploadedMediaType,
  handleDrivePreviewError,
  normalizeAttachmentRecord,
  uploadToMySkool,
} from "@/utils/gdriveUpload";

export default {
  name: "Resource",
  components: {
    BAvatar,
    BCard,
    BRow,
    BBadge,
    BTable,
    BCol,
    BFormInput,
    BButton,
    BSidebar,
    BFormGroup,
    BFormCheckbox,
    BForm,
    BImg,
    BPagination,
    BSpinner,
    vSelect,
    flatPickr,
    BModal,
  },
  props: {},
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
    Ripple,
  },
  async created() {
    const routeRights = this.$store.state.rights[this.$route.name];
    if (!routeRights) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = routeRights;
    await Promise.all([
      this.loadInfo(),
      this.loadResources(),
      this.LoadSettings(),
    ]);
  },

  mounted() {
    this.$nextTick(() => {
      this.updateScrollbarWidth();
    });

    this.handleClickOutside = (event) => {
      const clickedElement = event.target;
      const isVSelectClick =
        clickedElement.closest(".v-select") ||
        clickedElement.closest(".vs__dropdown-menu") ||
        clickedElement.closest(".vs__dropdown-option") ||
        clickedElement.closest(".vs__dropdown-toggle") ||
        clickedElement.closest(".vs__actions") ||
        clickedElement.closest(".vs__selected-options");

      if (!isVSelectClick) {
        const vSelectRefs = ["pageSizeSelect", "fil_class", "fil_sec"];
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName]?.$el) {
            const toggle = this.$refs[refName].$el.querySelector(
              ".vs__dropdown-toggle"
            );
            if (toggle) toggle.blur();
          }
        });

        const allToggles = document.querySelectorAll(".vs__dropdown-toggle");
        allToggles.forEach((toggle) => {
          if (document.activeElement === toggle) toggle.blur();
        });

        const dropdownMenus = document.querySelectorAll(".vs__dropdown-menu");
        dropdownMenus.forEach((menu) => {
          const style = window.getComputedStyle(menu);
          if (style.display !== "none" && style.visibility !== "hidden") {
            const allVSelects = document.querySelectorAll(".v-select");
            allVSelects.forEach((vSelect) => {
              const toggle = vSelect.querySelector(".vs__dropdown-toggle");
              if (toggle && document.activeElement === toggle) toggle.blur();
            });
          }
        });

        if (document.activeElement?.classList.contains("vs__dropdown-toggle")) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener("click", this.handleClickOutside, true);
    document.addEventListener("mousedown", this.handleClickOutside, true);
  },

  beforeDestroy() {
    if (this.handleClickOutside) {
      document.removeEventListener("click", this.handleClickOutside, true);
      document.removeEventListener("mousedown", this.handleClickOutside, true);
    }
  },
  computed: {
    ...mapGetters({ cmbDepartment: "cmbDepartment" }),
    ...mapGetters({ data: "data" }),

    tableBusy() {
      return this.dataLoading && this.resources.length > 0;
    },

    tableEmptyText() {
      return this.tableBusy ? "" : "No resources found";
    },

    isFormValid() {
      return (
        this.newResource.title &&
        this.myObj.clsID &&
        this.selectedSubject &&
        this.myObj.sec &&
        this.myObj.sec.length > 0
      );
    },

    displayedRange() {
      if (this.totalRecords === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
      return { start, end };
    },

    filters: function () {
      return this.items.filter((pro) => {
        return (
          pro.data.title.toLowerCase().match(this.searchQuery.toLowerCase()) ||
          pro.data.details.toLowerCase().match(this.searchQuery.toLowerCase())
        );
      });
    },
  },
  data() {
    return {
      titleText: "Resource Management",
      request: false,
      publishing: false,
      visibility: false,
      sidebarTitle: "Add Resource",
      sidebarButton: "Save",
      buttonIcon: "PlusIcon",
      dataLoading: true,
      searchQuery: "",
      currentPage: 1,
      pageSize: 20,
      totalRecords: 0,
      totalPages: 0,
      cache: {
        loadInfo: { data: null, timestamp: null, promise: null },
        loadResources: { data: null, timestamp: null, promise: null },
        loadSettings: { data: null, timestamp: null, promise: null },
      },
      CACHE_TTL: 2 * 60 * 1000,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "40", value: 40 },
        { label: "80", value: 80 },
        { label: "100", value: 100 },
      ],
      errors: {
        status: false,
      },
      index: null,
      fields: [
        {
          key: "title",
          label: "Title",
          sortable: false,
          thStyle: { width: "auto", minWidth: "250px", maxWidth: "500px" },
          tdClass: "title-cell",
        },
        { key: "class", label: "Class" },
        { key: "section", label: "Section" },
        { key: "subject", label: "Subject" },
        { key: "attachments", label: "Attachments" },
        {
          key: "date",
          label: "Date",
          thStyle: { minWidth: "140px", whiteSpace: "nowrap" },
        },
        { key: "actions", label: "Actions" },
      ],
      resources: [],
      selectedFiles: [],
      myObj: {
        campusID: this.$store.state.userData.cId,
        clsID: null,
        sec: [],
      },
      newResource: {
        id: 0,
        title: "",
        type: null,
        subjectId: 0,
        subject: "",
        description: "",
        file: null,
      },
      resourceTypes: [
        { value: "document", text: "Document" },
        { value: "video", text: "Video" },
        { value: "audio", text: "Audio" },
        { value: "presentation", text: "Presentation" },
        { value: "other", text: "Other" },
      ],
      isClassLoading: false,
      isSectionLoading: false,
      isSubjectLoading: false,
      isDragging: false,
      isUploading: false,
      isSidebarOpen: false,
      isDeleteModalOpen: false,
      resourceToDelete: null,
      classes: [],
      classesOptions: [],
      currentSections: [],
      subjects: [],
      selectedSubject: null,
      filterObj: {
        clsID: 0,
        secIDs: [0],
      },
      filterSections: [],
      attachments: [],
      mydocloading: false,
      editLoading: false,
      viewLoading: null,
      deleteLoading: null,
      currentID: 0,
      sidebarMode: "add",
      currentResourceId: 0,
      currentResourceSubjectId: null,
      linkInputs: [{ url: "", isValid: null }],
      showLinkInputs: false,
      resourcePublishSetting: null,
      publishModal: false,
      publishDateRange: null,
      dateRangeConfig: {
        mode: "range",
      },
      selectedIDs: [],
      bulkPublishing: false,
      bulkDeleting: false,
    };
  },
  validations: {
    age: {
      between: between(20, 30),
    },
  },
  watch: {
    selectedSubject: {
      handler() {},
      immediate: false,
    },
  },
  methods: {
    ...mapActions({ getDepartment: "getDepartment" }),
    ...mapActions({
      get: "get",
      post: "post",
      put: "put",
      delete: "delete",
      postObj: "postObj",
    }),

    getClassName(id) {
      let obj = this.classesOptions.find((el) => el.id == id);
      return obj ? obj.name : "-";
    },
    getSubName(id) {
      let obj = this.subjects.find((el) => el.id == id);
      return obj ? obj.subject : "-";
    },
    getSecName(clsID, id) {
      let obj = this.classes.find((el) => el.cls.id == clsID);
      if (obj) {
        let sec = obj.sections.find((el) => el.id == id);
        return sec ? sec.section : "-";
      } else return "-";
    },

    /** Resource row date: API `date` first, then common created* aliases */
    formatResourceDate(item) {
      if (!item || typeof item !== "object") return "—";
      const raw =
        item.date ??
        item.createdAt ??
        item.created_at ??
        item.createdOn ??
        item.created_on ??
        item.dateCreated ??
        item.date_created ??
        item.createdDate ??
        item.created ??
        null;
      if (raw === null || raw === undefined || raw === "") return "—";
      try {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return String(raw);
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        return String(raw);
      }
    },

    setSelectAll(e) {
      if (e) {
        this.selectedIDs = this.resources.map((el) => el.id);
      } else {
        this.selectedIDs = [];
      }
    },

    onRowSelect(resource) {
      if (
        resource &&
        this.resourcePublishSetting &&
        this.resourcePublishSetting !== false
      ) {
        if (this.selectedIDs.includes(resource.id)) {
          this.selectedIDs = this.selectedIDs.filter((el) => el != resource.id);
        } else {
          this.selectedIDs.push(resource.id);
        }
      }
    },

    setSelected(e, id) {
      if (e) {
        this.selectedIDs.push(id);
      } else {
        this.selectedIDs = this.selectedIDs.filter((el) => el != id);
      }
    },

    openPublishModal() {
      this.publishModal = true;
      this.publishDateRange = null;
    },

    async publishSelected() {
      if (this.selectedIDs.length > 0 && this.publishDateRange) {
        this.bulkPublishing = true;

        try {
          let startDate, endDate;
          if (
            Array.isArray(this.publishDateRange) &&
            this.publishDateRange.length === 2
          ) {
            startDate = this.publishDateRange[0];
            endDate = this.publishDateRange[1];
          } else if (
            typeof this.publishDateRange === "string" &&
            this.publishDateRange.includes(" to ")
          ) {
            const dates = this.publishDateRange.split(" to ");
            startDate = dates[0];
            endDate = dates[1];
          } else {
            throw new Error("Invalid date range format");
          }

          const formatDate = (date) => {
            const d = new Date(date);
            return d.toISOString().split("T")[0];
          };

          const formattedStartDate = formatDate(startDate);
          const formattedEndDate = formatDate(endDate);
          const resourceIDsString = this.selectedIDs.join(",");
          const publishUrl = `${this.$store.state.domain}Resources/Publish?db=${this.$store.state.userData.db}&resourceIDs=${resourceIDsString}&dtFrom=${formattedStartDate}&dtTo=${formattedEndDate}&userID=${this.$store.state.userData.userID}`;

          const obj = {
            url: publishUrl,
            token: this.$store.state.userData.token,
          };

          const res = await this.get(obj);

          if (res) {
            this.$bvToast.toast("Resources published successfully", {
              title: this.$t("Success"),
              variant: "success",
              solid: true,
            });

            this.selectedIDs = [];
            this.publishModal = false;
            this.publishDateRange = null;
            this.clearCache("loadResources");
            await this.loadResources(true);
          }
        } catch (error) {
          this.$bvToast.toast("Failed to publish resources", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.bulkPublishing = false;
        }
      } else {
        this.$bvToast.toast("Please select resources and date range", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
    },

    loadSections() {
      this.filterObj.secIDs = [0];
      if (this.filterObj.clsID === 0) {
        this.filterSections = [];
      } else {
        const classData = this.classes.find(
          (el) => el.cls.id === this.filterObj.clsID
        );
        this.filterSections = classData?.sections || [];
      }
      this.currentPage = 1;
      this.loadResources(true);
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadResources();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadResources(true);
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect?.$el) {
          const toggle = this.$refs.pageSizeSelect.$el.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) {
            toggle.blur();
            setTimeout(() => {
              document.body.click();
            }, 100);
          }
        }
      });
    },

    onSearchChange() {
      this.currentPage = 1;
      this.loadResources(true);
    },

    onSectionFilterChange() {
      this.currentPage = 1;
      this.loadResources(true);
    },

    syncScroll() {
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar.querySelector(
          ".table-scrollbar-content"
        );

        if (scrollContent) {
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (
            scrollContent &&
            tableWrapper.scrollWidth > tableWrapper.clientWidth
          ) {
            scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
            topScrollbar.addEventListener("scroll", () => {
              tableWrapper.scrollLeft = topScrollbar.scrollLeft;
            });
          }
        }
      });
    },

    async Edit(item) {
      if (!item) return;

      this.editLoading = true;
      this.currentID = item.data.id;

      this.attachments = [];
      this.myObj = {
        editIDs: [],
        homework: {},
        homeworkAttachment: [],
        sections: [],
        db: this.$store.state.userData.db,
      };

      var obj = {
        url:
          this.$store.state.domain +
          "Homework/GetSelected?id=" +
          item.data.id +
          "&db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
      };
      this.myObj.homework = await this.get(obj);
      this.isPublished = this.myObj.homework.isPublished;
      this.editLoading = false;
      this.myObj.editIDs = [item.data.id];
      this.attachments = (item.attachments || []).map((att) =>
        normalizeAttachmentRecord(att)
      );
      this.myObj.homeworkAttachment = [...this.attachments];

      this.visibility = true;
      this.FillSections();
      var elem = this.$refs["title"];
      elem.state = undefined;
      var elem = this.$refs["class"];
      elem.state = undefined;
      var elem = this.$refs["sub"];
      elem.state = undefined;
      var elem = this.$refs["section"];
      elem.state = undefined;
      var elem = this.$refs["date"];
      elem.state = undefined;

      this.sidebarTitle = "Edit Homework";
      this.sidebarButton = "Update";
    },

    AddOpen() {
      this.myObj = {
        editIDs: [],
        homework: {
          id: 0,
          subID: 0,
          cID: 0,
          title: "",
          section: 0,
          details: "",
          subject: "",
          date: new Date().toJSON(),
          savedBy: 0, // for admin
          campusID: this.$store.state.userData.cId,
          savedOn: new Date().toJSON(),
          attachments: [],
          type: "homework",
        },
        homeworkAttachment: [],
        sections: [],
        db: this.$store.state.userData.db,
      };
      this.attachments = [];
      this.isPublished = false;
      this.visibility = true;
      var elem = this.$refs["title"];
      elem.state = undefined;
      var elem = this.$refs["class"];
      elem.state = undefined;
      var elem = this.$refs["sub"];
      elem.state = undefined;
      var elem = this.$refs["section"];
      elem.state = undefined;

      this.sidebarTitle = "Add Homework";
      this.sidebarButton = "Save";
    },

    CheckTitle() {
      var elem = this.$refs["title"];
      if (this.myObj.homework.title == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkDetails() {
      var elem = this.$refs["detail"];
      if (this.myObj.homework.details.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckClass() {
      var elem = this.$refs["class"];
      if (this.myObj.homework.cID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSubject() {
      var elem = this.$refs["sub"];
      if (this.myObj.homework.subID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckSection() {
      var elem = this.$refs["section"];
      if (this.myObj.homework.section == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckDate() {
      var elem = this.$refs["date"];
      if (!this.myObj.homework.date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckMessage() {
      var elem = this.$refs["msg"];
      if (this.myObj.message == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    isImage(url) {
      return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
    },
    isDoc(url) {
      return /\.(pdf)$/.test(url);
    },
    removeMedia(index) {
      this.attachments.splice(index, 1);
    },
    addMedia() {
      const media = this.$refs.mediaDoc.files;
      if (!media || media.length === 0) return;

      const files = Array.from(media);
      const maxFileSize = 25 * 1024 * 1024;
      const oversizedFiles = files
        .filter((f) => f.size > maxFileSize)
        .map((f) => f.name);

      if (oversizedFiles.length > 0) {
        this.$bvToast.toast(
          `File size limit exceeded (25MB max): ${oversizedFiles.join(", ")}`,
          {
            title: "File Size Error",
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      this.mydocloading = true;
      uploadToMySkool(files)
        .then((uploaded) => {
          uploaded.forEach((el) => {
            this.attachments.push(
              buildUploadedAttachment(el, {
                id: 0,
                name: getUploadedMediaType(el),
              })
            );
          });
        })
        .catch(() => {
          this.$bvToast.toast("Failed to upload file", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        })
        .finally(() => {
          this.mydocloading = false;
          // allow re-selecting the same file(s)
          if (this.$refs.mediaDoc) this.$refs.mediaDoc.value = null;
        });
    },
    getType(url, mimetype, originalname) {
      return getUploadedMediaType({ path: url, mimetype, originalname });
    },
    getMediaPreviewUrl(url, type) {
      return resolveMediaPreviewUrl(url, type);
    },
    onMediaPreviewError(event, file) {
      handleDrivePreviewError(event, file?.url, file?.type);
    },
    openFile(url) {
      window.open(url, "_blank");
    },
    getFileIcon(type) {
      if (type) {
        if (type.toLowerCase() === "image") return "ImageIcon";
        else if (type.toLowerCase() === "video") return "VideoIcon";
        else if (type.toLowerCase() === "pdf") return "FileTextIcon";
        else return "LinkIcon";
      }
      return "LinkIcon";
    },

    hideSideBar() {
      this.visibility = false;
    },
    async loadInfo() {
      const cacheKey = "loadInfo";
      const now = Date.now();
      const cached = this.cache[cacheKey];

      if (
        cached?.data &&
        cached.timestamp &&
        now - cached.timestamp < this.CACHE_TTL
      ) {
        this.classes = cached.data.classes;
        this.classesOptions = cached.data.classesOptions;
        this.subjects = cached.data.subjects;
        return;
      }

      if (cached?.promise) {
        const result = await cached.promise;
        this.classes = result.classes;
        this.classesOptions = result.classesOptions;
        this.subjects = result.subjects;
        return;
      }

      this.isClassLoading = true;
      this.isSubjectLoading = true;

      const loadPromise = (async () => {
        try {
          const [classesRes, subjectsRes] = await Promise.all([
            this.get({
              url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&dID=0`,
              token: this.$store.state.userData.token,
            }),
            this.get({
              url: `${this.$store.state.domain}Subjects?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
              token: this.$store.state.userData.token,
            }),
          ]);

          const classes = Array.isArray(classesRes) ? classesRes : [];
          const classesOptions = classes.map((el) => el.cls);
          const subjects = Array.isArray(subjectsRes) ? subjectsRes : [];

          this.cache[cacheKey] = {
            data: { classes, classesOptions, subjects },
            timestamp: Date.now(),
            promise: null,
          };

          return { classes, classesOptions, subjects };
        } finally {
          this.isClassLoading = false;
          this.isSubjectLoading = false;
        }
      })();

      this.cache[cacheKey].promise = loadPromise;
      const result = await loadPromise;
      this.classes = result.classes;
      this.classesOptions = result.classesOptions;
      this.subjects = result.subjects;
    },

    async LoadData() {
      if (!this.filterObj.date) {
        this.$bvToast.toast("Please select the date.", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }
      this.dataLoading = true;
      this.selectedIDs = [];
      var obj = {
        url: this.$store.state.domain + "resources/LoadData",
        body: {
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          clsID: this.filterObj.clsID ? this.filterObj.clsID : 0,
          sections: this.filterObj.secID == 0 ? [] : [this.filterObj.secID],
          tID: 0,
          date: this.filterObj.date,
        },
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      };
      let res = await this.post(obj);
      if (Array.isArray(res)) {
        this.items = res;
      } else {
        this.items = [];
      }

      this.dataLoading = false;
    },

    async Add(val) {
      this.CheckTitle();
      this.CheckClass();
      this.CheckSubject();
      this.CheckSection();
      this.CheckDate();
      this.checkDetails();
      if (
        this.CheckSection() == false ||
        this.CheckClass() == false ||
        this.CheckSubject() == false ||
        this.checkDetails() == false ||
        this.CheckTitle() == false ||
        this.CheckDate() == false
      ) {
        return this.$bvToast.toast(this.$t("Please fill the form correctly."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } else {
        if (val) {
          this.publishing = true;
          this.myObj.homework.isPublished = true;
        } else {
          this.request = true;
        }
        this.myObj.sections = [this.myObj.homework.section];
        var status = await this.post({
          url: this.$store.state.domain + "Homework/Save",
          body: this.myObj,
          message: "Homework added successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        this.publishing = false;
        if (status) {
          this.LoadData();
          this.visibility = false;
        }
      }
    },

    async Delete(id) {
      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });
      if (result.isConfirmed) {
        var status = await this.delete({
          url:
            this.$store.state.domain +
            "Homework/DeleteHomework?db=" +
            this.$store.state.userData.db +
            "&id=" +
            id,
          body: null,
          message: "Homework removed successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });
        if (status) this.LoadData();
      }
    },

    async openMessagePopup(homework) {
      this.currentHomework = homework;
      this.messageModal = true;
      this.selectedStudents = [];
      this.studentList = [];
      this.messageText = "";
      this.loadingStudents = true;

      try {
        const obj = {
          url:
            this.$store.state.domain +
            "students/LoadDataClassWise?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&clsID=" +
            homework.data.cID +
            "&secIDs=" +
            homework.data.sectionID +
            "&gender=All",
          token: this.$store.state.userData.token,
        };
        const res = await this.get(obj);
        this.studentList = res.data || [];

        let message = `${homework.data.subject}:\n`;
        message += `This is homework for ${homework.data.clsName}-${homework.data.section}\n\n`;
        message += homework.data.details;

        if (homework.attachments && homework.attachments.length > 0) {
          message += "\n\nAttachments:\n";
          homework.attachments.forEach((att, index) => {
            message += `${index + 1}. ${att.url}\n`;
          });
        }

        this.messageText = message;
      } catch (error) {
        this.$bvToast.toast("Failed to load students", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
      this.loadingStudents = false;
    },

    dropFilter(opt, label, search) {
      return (
        opt.name.toLowerCase().match(search.toLowerCase()) ||
        opt.grNo.toLowerCase().match(search.toLowerCase()) ||
        opt.father_name.toLowerCase().match(search.toLowerCase())
      );
    },

    async sendMessage() {
      if (this.selectedStudents.length === 0) {
        this.$bvToast.toast("Please select at least one student", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (this.messageText.trim() == "") {
        this.$bvToast.toast(this.$t("Please enter message"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.sendingMessage = true;
      try {
        const studentIds = this.selectedStudents.map((s) => s.id);
        let cObj = {
          audio: "",
          media: "",
          message: this.messageText,
          sIDs: studentIds,
          tIDs: [],
          campusID: this.$store.state.userData.cId,
          userID: this.$store.state.userData.userID,
        };
        const obj = {
          url:
            this.$store.state.domain +
            "Chat/SendMessageSelected?db=" +
            this.$store.state.userData.db,
          body: cObj,
          message: "WhatsApp messages are being processed.",
          context: this,
          token: this.$store.state.userData.token,
        };

        await this.postObj(obj);
        this.messageModal = false;
      } catch (error) {
        this.$bvToast.toast("Failed to send message", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
      this.sendingMessage = false;
    },

    async handleUpload() {
      try {
        if (!this.newResource.title) {
          this.$bvToast.toast("Please enter a title", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        if (!this.myObj.clsID) {
          this.$bvToast.toast("Please select a class", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        if (!this.myObj.sec || this.myObj.sec.length === 0) {
          this.$bvToast.toast("Please select at least one section", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        if (!this.selectedSubject) {
          this.$bvToast.toast("Please select a subject", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        // Ensure selectedSubject is not null
        const subjectId =
          this.selectedSubject ||
          (this.sidebarMode === "edit" ? this.currentResourceSubjectId : null);
        if (!subjectId) {
          this.$bvToast.toast("Please select a valid subject", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        this.request = true;

        if (this.sidebarMode === "edit") {
          this.attachments = this.attachments.filter(
            (att) => att.type !== "Link"
          );
        }

        this.linkInputs.forEach((link) => {
          if (link.url?.trim() && link.isValid) {
            const url = link.url.trim();
            const existingAttachment = this.attachments.find(
              (att) => att.url === url && att.type === "Link"
            );
            if (!existingAttachment) {
              this.attachments.push({
                id: 0,
                name: this.extractDomainName(url),
                type: "Link",
                url: url,
                fileSize: 0,
              });
            }
          }
        });

        let sectionsToSend = [];
        if (this.sidebarMode === "edit") {
          sectionsToSend = [this.myObj.sec];
        } else {
          sectionsToSend = this.myObj.sec;
          if (this.myObj.sec?.includes(0)) {
            sectionsToSend = this.currentSections.map((section) => section.id);
          }
        }
        const payload = {
          editIDs: this.sidebarMode === "edit" ? [this.currentResourceId] : [],
          db: this.$store.state.userData.db,
          campusID: this.$store.state.userData.cId,
          sections: sectionsToSend,
          resources: {
            id: this.sidebarMode === "edit" ? this.currentResourceId : 0,
            title: this.newResource.title,
            subTitle: this.newResource.description || "",
            cID: this.myObj.clsID,
            subID: subjectId,
            tID: 0,
          },
          attachments: this.attachments.map((file) => ({
            fileSize: Number(file.fileSize || 0),
            type: file.type,
            url: file.url,
            name: file.name,
          })),
        };

        const status = await this.post({
          url:
            this.$store.state.domain +
            "Resources/Save?userID=" +
            this.$store.state.userData.userID,
          body: payload,
          message: "Resource saved successfully!",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.clearCache("loadResources");
          this.toggleSidebar(false);
          await this.loadResources(true);
        }
      } catch (error) {
        this.$bvToast.toast(
          "Failed to save resource: " + (error.message || "Unknown error"),
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.request = false;
      }
    },

    async loadResources(forceRefresh = false) {
      const cacheKey = "loadResources";
      const cacheParams = JSON.stringify({
        clsID: this.filterObj.clsID || 0,
        secIDs: this.filterObj.secIDs || [0],
        page: this.currentPage,
        pageSize: this.pageSize,
        search: this.searchQuery?.trim() || "",
      });

      if (!forceRefresh) {
        const cached = this.cache[cacheKey];
        if (
          cached?.data?.[cacheParams] &&
          cached.timestamp &&
          Date.now() - cached.timestamp < this.CACHE_TTL
        ) {
          const cachedData = cached.data[cacheParams];
          this.resources = cachedData.resources;
          this.totalRecords = cachedData.totalRecords;
          this.totalPages = cachedData.totalPages;
          return;
        }

        if (cached?.promise) {
          const result = await cached.promise;
          this.resources = result.resources;
          this.totalRecords = result.totalRecords;
          this.totalPages = result.totalPages;
          return;
        }
      }

      try {
        this.dataLoading = true;
        this.selectedIDs = [];

        const sectionsArray =
          this.filterObj.secIDs &&
          this.filterObj.secIDs.length > 0 &&
          !this.filterObj.secIDs.includes(0)
            ? this.filterObj.secIDs.filter((id) => id !== 0)
            : [];

        const loadPromise = this.post({
          url: `${this.$store.state.domain}Resources/LoadData`,
          body: {
            db: this.$store.state.userData.db,
            cID: this.$store.state.userData.cId,
            clsID: this.filterObj.clsID || 0,
            sections: sectionsArray,
            tID: 0,
            page: this.currentPage,
            pageSize: this.pageSize,
            search: this.searchQuery?.trim() || "",
          },
          context: this,
          token: this.$store.state.userData.token,
          allResponse: true,
        });

        if (!this.cache[cacheKey]) {
          this.cache[cacheKey] = { data: {}, timestamp: null, promise: null };
        }
        this.cache[cacheKey].promise = loadPromise;

        const response = await loadPromise;

        let resources = [];
        let totalRecords = 0;
        let totalPages = 0;

        if (response) {
          if (
            response.totalCount !== undefined &&
            response.totalCount !== null
          ) {
            resources = Array.isArray(response.data) ? response.data : [];
            totalRecords = response.totalCount;
            totalPages =
              response.totalPages || Math.ceil(totalRecords / this.pageSize);
          } else if (
            response.totalRecords !== undefined &&
            response.totalRecords !== null
          ) {
            resources = Array.isArray(response.data) ? response.data : [];
            totalRecords = response.totalRecords;
            totalPages =
              response.totalPages || Math.ceil(totalRecords / this.pageSize);
          } else if (
            response.data?.totalCount !== undefined &&
            response.data.totalCount !== null
          ) {
            resources = Array.isArray(response.data.data)
              ? response.data.data
              : Array.isArray(response.data)
              ? response.data
              : [];
            totalRecords = response.data.totalCount;
            totalPages =
              response.data.totalPages ||
              Math.ceil(totalRecords / this.pageSize);
          } else if (Array.isArray(response.data)) {
            resources = response.data;
            totalRecords = response.data.length;
            totalPages = Math.ceil(totalRecords / this.pageSize);
          } else if (Array.isArray(response)) {
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = startIndex + this.pageSize;
            resources = response.slice(startIndex, endIndex);
            totalRecords = response.length;
            totalPages = Math.ceil(totalRecords / this.pageSize);
          }
        }

        this.resources = resources;
        this.totalRecords = totalRecords;
        this.totalPages = totalPages;

        if (!this.cache[cacheKey].data) {
          this.cache[cacheKey].data = {};
        }
        this.cache[cacheKey].data[cacheParams] = {
          resources,
          totalRecords,
          totalPages,
        };
        this.cache[cacheKey].timestamp = Date.now();
        this.cache[cacheKey].promise = null;
      } catch (error) {
        this.$bvToast.toast("Failed to load resources", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
        this.resources = [];
        this.totalRecords = 0;
        this.totalPages = 0;
        if (this.cache[cacheKey]) {
          this.cache[cacheKey].promise = null;
        }
      } finally {
        this.dataLoading = false;
        this.$nextTick(() => {
          this.updateScrollbarWidth();
        });
      }
    },

    toggleSidebar(isOpen) {
      this.isSidebarOpen = isOpen;
      if (isOpen && this.sidebarMode === "add") {
        this.resetForm();
      }
      if (!isOpen) {
        this.resetForm();
      }
    },
    onSidebarHidden() {
      this.isSidebarOpen = false;
      this.resetForm();
    },
    onSidebarChange(val) {
      this.isSidebarOpen = val;
      if (!val) {
        this.resetForm();
      }
    },
    resetForm() {
      this.newResource = {
        id: 0,
        title: "",
        type: null,
        subjectId: 0,
        subject: "",
        description: "",
        file: null,
      };
      this.selectedFiles = [];
      this.myObj = {
        campusID: this.$store.state.userData.cId,
        clsID: null,
        sec: [],
      };
      this.selectedSubject = null;
      this.attachments = [];
      this.linkInputs = [{ url: "", isValid: null }]; // Reset link inputs
      this.showLinkInputs = false;
      this.sidebarMode = "add";
      this.currentResourceId = 0;
      this.currentResourceSubjectId = null; // Store original subject ID for backup
      this.sidebarTitle = "Add Resource";
      this.sidebarButton = "Save Resource";
    },
    onDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    onDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const files = event.dataTransfer.files;
      this.onFileSelect(files);
    },
    onFileSelect(event) {
      const files = event.target ? event.target.files : event;

      // Check file size limit (25MB = 25 * 1024 * 1024 bytes)
      const maxFileSize = 25 * 1024 * 1024; // 25MB in bytes
      const oversizedFiles = [];

      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxFileSize) {
          oversizedFiles.push(files[i].name);
        }
      }

      if (oversizedFiles.length > 0) {
        this.$bvToast.toast(
          `File size limit exceeded (25MB max): ${oversizedFiles.join(", ")}`,
          {
            title: "File Size Error",
            variant: "danger",
            solid: true,
          }
        );
        return;
      }

      this.selectedFiles = Array.from(files).map((file) => ({
        name: file.name,
        type: this.getType(file.name),
        size: file.size,
        file: file,
      }));

      // Upload files first
      this.uploadFiles();
    },
    async uploadFiles() {
      try {
        this.isUploading = true;
        const uploadedFiles = await uploadToMySkool(
          this.selectedFiles.map((fileObj) => fileObj.file)
        );

        this.selectedFiles = this.selectedFiles.map((file, index) => ({
          ...file,
          url: uploadedFiles[index].path,
          resourceType: getUploadedMediaType(uploadedFiles[index]),
          resourceName: file.name,
          resourceFilePath: uploadedFiles[index].path,
        }));
      } catch (error) {
        this.$bvToast.toast("Failed to upload files", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isUploading = false;
      }
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    formatDate(date) {
      const formattedDate = new Date(date).toLocaleDateString("en-UK", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      return formattedDate;
    },
    formatFileSize(size) {
      const units = ["B", "KB", "MB", "GB"];
      let unitIndex = 0;
      let formattedSize = size;
      while (formattedSize >= 1024 && unitIndex < units.length - 1) {
        formattedSize /= 1024;
        unitIndex++;
      }
      return `${formattedSize.toFixed(2)} ${units[unitIndex]}`;
    },
    getTypeVariant(type) {
      const typeVariants = {
        homework: "primary",
        classwork: "secondary",
        Image: "success",
        Video: "info",
        pdf: "warning",
        Link: "danger",
      };
      return typeVariants[type] || "secondary";
    },
    FillSections() {
      this.currentSections = [];
      // Don't reset section selection in edit/view mode
      if (this.sidebarMode === "add") {
        this.myObj.sec = [];
      }

      this.isSectionLoading = true;
      if (this.myObj.clsID) {
        const classData = this.classes.find(
          (elem) => elem.cls.id === this.myObj.clsID
        );
        if (classData && classData.sections.length > 0) {
          this.currentSections = classData.sections;
        }
      }
      this.isSectionLoading = false;
    },

    getSectionDisplayName() {
      if (!this.myObj.sec || this.myObj.sec.length === 0) return "No section";
      if (Array.isArray(this.myObj.sec)) {
        return this.myObj.sec
          .map((secId) => this.getSecName(this.myObj.clsID, secId))
          .join(", ");
      }
      return this.getSecName(this.myObj.clsID, this.myObj.sec);
    },

    handleEditSectionChange(selectedSectionId) {
      // Update section data for edit mode
      this.myObj.sec = selectedSectionId;
    },

    handleSectionSelection(selectedSections) {
      if (!selectedSections || selectedSections.length === 0) {
        this.myObj.sec = [];
        return;
      }

      // If "All Sections" (0) is selected
      if (selectedSections.includes(0)) {
        // If user just selected "All Sections", remove all other selections
        if (
          selectedSections.length > 1 &&
          selectedSections[selectedSections.length - 1] === 0
        ) {
          this.myObj.sec = [0];
        }
        // If user selected a specific section while "All Sections" was already selected, remove "All Sections"
        else if (selectedSections.length > 1) {
          this.myObj.sec = selectedSections.filter((id) => id !== 0);
        }
        // Only "All Sections" is selected
        else {
          this.myObj.sec = [0];
        }
      } else {
        // No "All Sections" selected, keep the selection as is
        this.myObj.sec = selectedSections;
      }
    },

    viewResource(resource) {
      this.viewLoading = resource.id;
      this.sidebarMode = "view";
      this.currentResourceId = resource.id;
      this.currentResourceSubjectId = resource.subjectId || resource.subID;
      this.sidebarTitle = "View Resource";

      // Populate form with resource data in view mode
      this.newResource.title = resource.title || "";
      this.myObj.clsID = resource.classId || resource.cID;
      this.selectedSubject = resource.subjectId || resource.subID;

      // Load sections for the selected class
      this.FillSections();

      // Use nextTick to ensure sections are populated before setting the selected section
      this.$nextTick(() => {
        // Set sections for view mode
        let sectionId = null;
        if (resource.sectionIds) {
          sectionId = Array.isArray(resource.sectionIds)
            ? resource.sectionIds[0]
            : resource.sectionIds;
        } else if (resource.sections) {
          sectionId = Array.isArray(resource.sections)
            ? resource.sections[0]
            : resource.sections;
        } else if (resource.section) {
          sectionId = resource.section;
        }

        // Find the matching section in currentSections
        const matchingSection = this.currentSections.find(
          (sec) => sec.id == sectionId
        );

        if (matchingSection) {
          this.myObj.sec = matchingSection.id;
        } else if (this.currentSections.length > 0) {
          // Fallback to first section if available
          this.myObj.sec = this.currentSections[0].id;
        }
      });

      // Set attachments and extract links
      this.attachments = resource.attachments || [];
      this.populateLinkInputs();

      this.toggleSidebar(true);
      this.viewLoading = null;
    },

    async editResource(resource) {
      this.editLoading = true;
      this.currentID = resource.id;
      this.sidebarMode = "edit";
      this.currentResourceId = resource.id;
      this.currentResourceSubjectId = resource.subjectId || resource.subID;
      this.sidebarTitle = "Edit Resource";
      this.sidebarButton = "Update Resource";

      try {
        // Set form data for editing
        this.newResource.title = resource.title || "";
        this.myObj.clsID = resource.classId || resource.cID;
        this.selectedSubject = resource.subjectId || resource.subID;

        // Load sections for the selected class first
        this.FillSections();

        // Use nextTick to ensure sections are populated before setting the selected section
        this.$nextTick(() => {
          // Set sections for edit mode (single section)
          let sectionId = null;
          if (resource.sectionIds) {
            sectionId = Array.isArray(resource.sectionIds)
              ? resource.sectionIds[0]
              : resource.sectionIds;
          } else if (resource.sections) {
            sectionId = Array.isArray(resource.sections)
              ? resource.sections[0]
              : resource.sections;
          } else if (resource.section) {
            sectionId = resource.section;
          }

          // Find the matching section in currentSections
          const matchingSection = this.currentSections.find(
            (sec) => sec.id == sectionId
          );

          if (matchingSection) {
            this.myObj.sec = matchingSection.id;
          } else {
            // Fallback to first section if available
            if (this.currentSections.length > 0) {
              this.myObj.sec = this.currentSections[0].id;
            }
          }
        });

        // Set attachments and extract links
        this.attachments = resource.attachments || [];
        this.populateLinkInputs();

        this.toggleSidebar(true);
      } catch (error) {
        this.$bvToast.toast("Failed to load resource for editing", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.editLoading = false;
      }
    },

    async handleDelete(resource) {
      if (!this.rights.delete) return;

      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        this.deleteLoading = resource.id;
        try {
          const status = await this.delete({
            url:
              this.$store.state.domain +
              "Resources/DeleteSelected?db=" +
              this.$store.state.userData.db +
              "&resourceIDs=" +
              resource.id +
              "&userID=" +
              this.$store.state.userData.userID,
            body: null,
            message: "Resource deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });

          if (status) {
            this.clearCache("loadResources");
            await this.loadResources(true);
          }
        } catch (error) {
          this.$bvToast.toast("Failed to delete resource", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.deleteLoading = null;
        }
      }
    },

    async handleBulkDelete() {
      if (this.selectedIDs.length === 0) return;

      let result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: `You are about to delete ${this.selectedIDs.length} resources. This action cannot be undone!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete all!",
      });

      if (result.isConfirmed) {
        this.bulkDeleting = true;
        try {
          const resourceIDsString = this.selectedIDs.join(",");
          const deleteUrl = `${this.$store.state.domain}Resources/DeleteSelected?db=${this.$store.state.userData.db}&resourceIDs=${resourceIDsString}&userID=${this.$store.state.userData.userID}`;

          const status = await this.delete({
            url: deleteUrl,
            body: null,
            message: "Selected resources deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });

          if (status) {
            this.selectedIDs = [];
            this.clearCache("loadResources");
            await this.loadResources(true);
          }
        } catch (error) {
          this.$bvToast.toast("Failed to delete selected resources", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        } finally {
          this.bulkDeleting = false;
        }
      }
    },

    populateLinkInputs() {
      // For edit/view mode, populate link inputs with existing links
      if (this.sidebarMode === "edit") {
        const existingLinks = this.attachments.filter(
          (att) => att.type === "Link"
        );
        if (existingLinks.length > 0) {
          // Populate linkInputs with existing links for editing
          this.linkInputs = existingLinks.map((link) => ({
            url: link.url,
            isValid: true,
          }));

          this.showLinkInputs = true;
        } else {
          this.linkInputs = [{ url: "", isValid: null }];
          this.showLinkInputs = false;
        }
      } else {
        // For add/view mode, just reset
        this.linkInputs = [{ url: "", isValid: null }];
        this.showLinkInputs = false;
      }
    },

    addLinkInput() {
      this.linkInputs.push({ url: "", isValid: null });
    },

    removeLinkInput(index) {
      this.linkInputs.splice(index, 1);
      // if (this.linkInputs.length > 1) {
      // } else {
      //   // If it's the last input, just clear it but keep the input field
      //   this.linkInputs[0] = { url: "", isValid: null };
      // }
    },

    validateAndAddLink(index) {
      const link = this.linkInputs[index];
      if (link.url && link.url.trim() !== "") {
        let url = link.url.trim();

        // Validate URL format
        const urlPattern =
          /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

        if (!urlPattern.test(url)) {
          link.isValid = false;
          this.$bvToast.toast("Please enter a valid URL", {
            title: "Invalid Link",
            variant: "danger",
            solid: true,
          });
          return false;
        }

        // Add https:// if no protocol is specified
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = "https://" + url;
          link.url = url;
        }

        // Mark as valid
        link.isValid = true;
        return true;
      } else {
        link.isValid = null;
        return true; // Empty is valid
      }
    },

    extractDomainName(url) {
      try {
        const domain = new URL(url).hostname;
        return domain.replace("www.", "");
      } catch (e) {
        return "External Link";
      }
    },

    removeLinkFromAttachments(index) {
      const linkAttachments = this.attachments.filter(
        (el) => el.type === "Link"
      );
      const linkToRemove = linkAttachments[index];

      // Remove from attachments
      this.attachments = this.attachments.filter(
        (att) => !(att.type === "Link" && att.url === linkToRemove.url)
      );
    },

    clearCache(cacheKey = null) {
      if (cacheKey) {
        if (this.cache[cacheKey]) {
          this.cache[cacheKey] = { data: null, timestamp: null, promise: null };
        }
      } else {
        Object.keys(this.cache).forEach((key) => {
          this.cache[key] = { data: null, timestamp: null, promise: null };
        });
      }
    },

    async LoadSettings() {
      const cacheKey = "loadSettings";
      const now = Date.now();
      const cached = this.cache[cacheKey];

      if (
        cached?.data &&
        cached.timestamp &&
        now - cached.timestamp < this.CACHE_TTL
      ) {
        this.resourcePublishSetting = cached.data;
        return;
      }

      if (cached?.promise) {
        this.resourcePublishSetting = await cached.promise;
        return;
      }

      try {
        const loadPromise = this.post({
          url: `${this.$store.state.domain}Settings/getSettingsAll?db=${this.$store.state.userData.db}`,
          body: ["resource_publish"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });

        this.cache[cacheKey] = {
          data: null,
          timestamp: null,
          promise: loadPromise,
        };

        const response = await loadPromise;
        const setting = Array.isArray(response)
          ? response.find((el) => el.key === "resource_publish")
          : null;

        this.resourcePublishSetting = setting;
        this.cache[cacheKey] = {
          data: setting,
          timestamp: Date.now(),
          promise: null,
        };
      } catch (error) {
        this.resourcePublishSetting = null;
        if (this.cache[cacheKey]) {
          this.cache[cacheKey].promise = null;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

/* Keyframe animations - Soft landing */
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Resources List Card - Now includes filter row
.resources-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible; // Changed from hidden to visible to allow dropdowns to show properly
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem; // Reduced margin to touch curve
    right: 0.75rem; // Reduced margin to touch curve
    width: calc(100% - 1.5rem); // Subtract left and right padding
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Remove all background layers from v-select to prevent double layer
  ::v-deep .v-select {
    position: relative;
    z-index: 10;
    background: transparent !important;
    box-shadow: none !important;

    &::before,
    &::after {
      display: none !important;
    }
  }

  ::v-deep .v-select .vs__dropdown-toggle {
    border-radius: 10px !important;
    min-height: 42px !important;
    height: 42px !important;
    border: 1.5px solid #e0d8ff !important;
    background: #fff !important;
    transition: all 0.2s ease;
    box-shadow: none !important;
    position: relative;
    z-index: 10;
    padding: 0.5rem 0.75rem !important;

    &::before,
    &::after {
      display: none !important;
    }

    &:hover {
      border-color: #b8a9ff !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }
  }

  // Remove any wrapper backgrounds
  ::v-deep .v-select .vs__dropdown-toggle > * {
    background: transparent !important;
  }

  ::v-deep .v-select .vs__selected-options {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .v-select .vs__search-container {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .v-select .vs__selected {
    background: transparent !important;
  }

  ::v-deep .v-select .vs__search {
    background: transparent !important;
  }

  ::v-deep .v-select .vs__dropdown-menu {
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    background: #ffffff !important;
    z-index: 10000 !important;
    position: fixed !important;
    margin-top: 4px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    min-width: 200px !important;

    // Remove any duplicate backgrounds
    &::before,
    &::after {
      display: none !important;
    }

    .vs__dropdown-option {
      background-color: #ffffff !important;
      background-image: none !important;
      padding: 0.5rem 0.75rem !important;
      color: #2d1b69 !important;
      font-weight: 500 !important;

      &::before,
      &::after {
        display: none !important;
      }

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
      }

      &--highlight,
      &--selected {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600 !important;
      }

      &--selected {
        background-color: #f3f0ff !important;
        color: #7c5dfa !important;
        font-weight: 600 !important;
      }
    }
  }

  ::v-deep .v-select .vs__dropdown-option {
    padding: 0.5rem 0.75rem;
    color: #2d1b69 !important;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
      opacity: 1 !important;
    }

    &--highlight {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600;
      opacity: 1 !important;
    }
  }

  ::v-deep .v-select .vs__selected {
    color: #2d1b69 !important;
    font-weight: 600;
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .v-select .vs__search {
    color: #2d1b69 !important;
    font-weight: 500;
    padding: 0.5rem;
    background: transparent !important;
    margin: 0 !important;
  }

  ::v-deep .v-select .vs__clear {
    color: #6b7280;
    cursor: pointer;
    transition: color 0.2s ease;
    margin: 0 !important;
    padding: 0 !important;

    &:hover {
      color: #7c5dfa;
    }
  }

  ::v-deep .v-select .vs__actions {
    padding: 0 !important;
    margin: 0 !important;
  }

  label {
    font-weight: 700;
    color: #2d1b69;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
  }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;

  @media (min-width: 1200px) {
    flex-wrap: nowrap;
  }

  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
    background: transparent !important;
  }

  // Ensure buttons column is right-aligned
  > [class*="col-"].text-right {
    justify-content: flex-end;
  }
}

// Filter Control - Consistent height and styling for all filters
.filter-control {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  background: #fff;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;

  &:hover {
    border-color: #b8a9ff;
  }

  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
}

// v-select filter control styling - Remove all duplicate backgrounds
.filter-control.v-select {
  width: 100%;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;

  &::before,
  &::after {
    display: none !important;
  }

  ::v-deep .v-select {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    margin: 0 !important;

    &::before,
    &::after {
      display: none !important;
    }
  }

  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    min-height: 42px !important;
    border-radius: 10px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.5rem 0.75rem !important;
    background: #fff !important;
    transition: all 0.2s ease;
    box-shadow: none !important;
    margin: 0 !important;

    &::before,
    &::after {
      display: none !important;
    }

    // Remove any wrapper backgrounds
    > * {
      background: transparent !important;
    }

    &:hover {
      border-color: #b8a9ff !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
      background: #fff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
      background: #fff !important;
    }
  }

  // Remove any wrapper backgrounds
  ::v-deep .vs__dropdown-toggle > * {
    background: transparent !important;
  }

  ::v-deep .vs__selected-options {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__search-container {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__selected {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__search {
    background: transparent !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 !important;
    margin: 0 !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 10000 !important;
    position: fixed !important;
    background: #ffffff !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    border-radius: 8px !important;
    margin-top: 4px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    min-width: 200px !important;

    &::before,
    &::after {
      display: none !important;
    }

    .vs__dropdown-option {
      background-color: #ffffff !important;
      background-image: none !important;
      padding: 0.5rem 0.75rem !important;
      color: #2d1b69 !important;
      font-weight: 500 !important;

      &::before,
      &::after {
        display: none !important;
      }

      &:hover {
        background-color: #f9fafb !important;
        background-image: none !important;
        color: #1f2937 !important;
      }

      &--highlight,
      &--selected {
        background-color: #f3f4f6 !important;
        background-image: none !important;
        color: #1f2937 !important;
        font-weight: 600 !important;
      }

      &--selected {
        background-color: #f3f0ff !important;
        color: #7c5dfa !important;
        font-weight: 600 !important;
      }
    }
  }
}

// Action Buttons
.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Resources List Card
.resources-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: visible; // Allow dropdowns to show
  animation: slideUpFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  z-index: 1;

  // Gradient Line Wrapper - Aligned with content, shorter length
  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem; // Reduced margin to touch curve
    right: 0.75rem; // Reduced margin to touch curve
    width: calc(100% - 1.5rem); // Subtract left and right padding
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }

  &:hover {
    box-shadow: 0 15px 40px rgba(115, 103, 240, 0.2);
    transform: translateY(-2px);
  }

  // Filter row styling inside card
  .filter-row-single-line {
    background: transparent !important;
    padding: 0;
    margin-bottom: 1rem;
  }

  // v-select styling for dropdowns inside card
  ::v-deep .v-select {
    position: relative;
    z-index: 10;
    background: transparent !important;
    box-shadow: none !important;

    &::before,
    &::after {
      display: none !important;
    }
  }

  ::v-deep .v-select .vs__dropdown-toggle {
    border-radius: 10px !important;
    min-height: 42px !important;
    height: 42px !important;
    border: 1.5px solid #e0d8ff !important;
    background: #fff !important;
    transition: all 0.2s ease;
    box-shadow: none !important;
    position: relative;
    z-index: 10;
    padding: 0.5rem 0.75rem !important;

    &::before,
    &::after {
      display: none !important;
    }

    &:hover {
      border-color: #b8a9ff !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .v-select .vs__dropdown-menu {
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    background: #ffffff !important;
    z-index: 10000 !important;
    position: fixed !important;
    margin-top: 4px !important;
    max-height: 300px !important;
    overflow-y: auto !important;
    min-width: 200px !important;
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 500;
  }
}

// Table Wrapper for Horizontal Scroll
.table-scroll-container {
  position: relative;
  width: 100%;
}

// Top Scrollbar for PC Users
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.table-wrapper {
  position: relative;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch;

  // Bottom scrollbar styling
  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  // Firefox scrollbar
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

// Resources Table - Modern Compact Design
.resources-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;

    &.title-cell {
      vertical-align: top !important;
      white-space: normal !important;
      word-break: break-word !important;
      word-wrap: break-word !important;
      max-width: 500px !important;
      min-width: 250px !important;
      width: auto !important;
      padding: 0.75rem 1rem !important;
      line-height: 1.6 !important;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;

      td {
        background: #f8fafc;
      }
    }

    &:nth-child(odd) {
      background: #ffffff;

      td {
        background: #ffffff;
      }
    }

    &:hover {
      background: #f1f5f9 !important;
      border-left-color: #7c5dfa;
      box-shadow: -2px 0 8px rgba(124, 93, 250, 0.1);
      z-index: 1;
      position: relative;

      td {
        background: transparent !important;
        color: #1e293b;
        font-weight: 500;
      }
    }

    &:active {
      transform: none;
    }
  }

  ::v-deep .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-weight: 600;
    line-height: 1.2;
  }

  // Compact cell content
  ::v-deep .text-muted {
    font-size: 0.8rem;
    color: #64748b;
  }
}

// Busy state: slot is rendered inside a single colspan cell — flex mimics table columns
.resource-table-busy-skeleton {
  width: 100%;
  padding: 0.35rem 0.15rem;
  box-sizing: border-box;
}

.busy-skeleton-row {
  display: flex;
  align-items: stretch;
  gap: 0.65rem;
  margin-bottom: 0.55rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.busy-skeleton-cell {
  flex: 1 1 0;
  min-width: 52px;
  padding: 0.35rem 0.2rem;
  box-sizing: border-box;

  &:nth-child(1) {
    flex: 2.2 1 220px;
    min-width: 160px;
  }
  &:nth-child(2) {
    flex: 0.65 1 70px;
    min-width: 64px;
  }
  &:nth-child(3) {
    flex: 1 1 96px;
    min-width: 88px;
  }
  &:nth-child(4) {
    flex: 1 1 104px;
    min-width: 92px;
  }
  &:nth-child(5) {
    flex: 1 1 96px;
    min-width: 88px;
  }
  &:nth-child(6) {
    flex: 1 1 112px;
    min-width: 100px;
  }
  &:nth-child(7) {
    flex: 0.9 1 104px;
    min-width: 96px;
  }

  .skeleton-loader {
    width: 100%;
    min-height: 18px;
  }
}

// Skeleton Loader Styles - Premium Animated Effect
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  transition: opacity 0.3s ease;

  &:nth-child(1) {
    animation-delay: 0s;
  }

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }

  &:nth-child(4) {
    animation-delay: 0.45s;
  }

  &:nth-child(5) {
    animation-delay: 0.6s;
  }

  &:hover {
    background: transparent !important;
    border-left-color: transparent !important;
    box-shadow: none !important;
    transform: none !important;

    td {
      background: transparent !important;
      color: inherit !important;
      font-weight: normal !important;
    }
  }
}

.skeleton-cell {
  padding: 0.875rem 0.875rem !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f5f9 !important;
  border-bottom: 1px solid #f8fafc !important;
  background: #ffffff !important;
  position: relative;

  &:nth-child(even) {
    background: #fafbfc !important;
  }

  // Add subtle border animation
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.03) 50%,
      transparent 100%
    );
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
    pointer-events: none;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  // Premium shimmer effect
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
    filter: blur(8px);
  }

  // Secondary glow layer
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(124, 93, 250, 0.08) 30%,
      rgba(157, 122, 255, 0.12) 50%,
      rgba(124, 93, 250, 0.08) 70%,
      transparent 100%
    );
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

// Different widths for different column positions
.skeleton-cell {
  &:nth-child(1) .skeleton-loader {
    width: 50px;
    margin: 0 auto;
  }

  &:nth-child(2) .skeleton-loader {
    width: 70px;
  }

  &:nth-child(3) .skeleton-loader {
    width: 140px;
  }

  &:nth-child(4) .skeleton-loader {
    width: 110px;
  }

  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }

  &:nth-child(7) .skeleton-loader {
    width: 60px;
  }

  &:nth-child(8) .skeleton-loader {
    width: 90px;
  }

  &:nth-child(9) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(10) .skeleton-loader {
    width: 180px;
  }

  &:nth-child(11) .skeleton-loader {
    width: 120px;
  }

  &:nth-child(12) .skeleton-loader {
    width: 100px;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes skeletonColorWave {
  0%,
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.6;
    transform: scaleX(1);
  }
}

@keyframes skeletonBorderGlow {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

// Title Cell Styling - Matching commentbank.vue with shorter width
.title-cell {
  white-space: normal !important;
  word-break: break-word !important;
  word-wrap: break-word !important;
  vertical-align: top !important;
  max-width: 500px !important;
  min-width: 250px !important;
  width: auto !important;
  padding: 0.75rem 1rem !important;
  line-height: 1.6 !important;
}

.title-cell-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;

  .title-checkbox-wrapper,
  .title-text-wrapper {
    width: 100%;
    display: flex;
    align-items: flex-start;

    ::v-deep .custom-control {
      display: flex;
      align-items: flex-start;
      width: 100%;

      .custom-control-label {
        width: 100%;
        padding-left: 1.5rem;
        cursor: pointer;

        &::before,
        &::after {
          top: 0.25rem;
        }
      }
    }
  }

  .title-text {
    white-space: normal !important;
    word-break: break-word !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    display: block;
    line-height: 1.6;
    width: 100%;
    max-width: 100%;
  }

  .title-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-top: 0.25rem;
  }
}

@media (max-width: 900px) {
  .title-cell {
    min-width: 150px !important;
    max-width: 250px !important;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.content-sidebar-header {
  border-bottom: 1px solid #ebe9f1;
}

// Hide the empty sidebar header
::v-deep #add-resource-sidebar .b-sidebar-header {
  display: none !important;
}

// Modern Sidebar Styling
.resource-sidebar {
  ::v-deep .b-sidebar-body {
    padding: 0 !important;
    overflow-y: auto;
  }
}

.sidebar-header-modern {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border-bottom: 2px solid #e0d8ff;
  padding: 1.25rem 1.5rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .sidebar-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;
  }

  .sidebar-title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .sidebar-title-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
  }

  .sidebar-title {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
    letter-spacing: -0.3px;
    margin: 0;
  }

  .sidebar-close-btn {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #e0d8ff;
    background: #fff;
    color: #64748b;
    transition: all 0.3s ease;

    &:hover {
      background: #f3f0ff;
      border-color: #b8a9ff;
      color: #7c5dfa;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(124, 93, 250, 0.15);
    }
  }
}

.sidebar-body-modern {
  padding: 1.5rem;
  background: #ffffff;
  min-height: calc(100vh - 80px);
}

.sidebar-form-modern {
  .modern-form-group {
    margin-bottom: 1.5rem;

    label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
      display: block;
    }
  }

  .modern-input {
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding: 0.625rem 0.875rem;
    min-height: 44px;
    background: #fff;
    transition: all 0.2s ease;
    color: #1e293b;
    font-weight: 500;
    width: 100%;

    &:hover {
      border-color: #b8a9ff;
    }

    &:focus {
      border-color: #7c5dfa;
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      outline: none;
    }

    &[readonly] {
      background: #f8fafc;
      color: #64748b;
    }

    &.is-invalid {
      border-color: #ef4444;
    }
  }

  .modern-select {
    ::v-deep .vs__dropdown-toggle {
      border-radius: 10px;
      border: 1.5px solid #e0d8ff;
      padding: 0.625rem 0.875rem;
      min-height: 44px;
      background: #fff;
      transition: all 0.2s ease;

      &:hover {
        border-color: #b8a9ff;
      }

      &:focus-within {
        border-color: #7c5dfa;
        box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
      }
    }

    &.is-invalid {
      ::v-deep .vs__dropdown-toggle {
        border-color: #ef4444;
      }
    }
  }

  .modern-display-text {
    padding: 0.625rem 0.875rem;
    background: #f8fafc;
    border: 1.5px solid #e0d8ff;
    border-radius: 10px;
    color: #1e293b;
    font-weight: 500;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .modern-btn-outline {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.625rem 1rem;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .modern-btn-icon {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modern-btn-primary {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.75rem 1.25rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

.sidebar-footer-modern {
  padding: 1.5rem;
  background: #faf9ff;
  border-top: 2px solid #e0d8ff;
  position: sticky;
  bottom: 0;
  margin: 1.5rem -1.5rem -1.5rem -1.5rem;

  .modern-btn-submit {
    border-radius: 10px;
    font-weight: 600;
    padding: 0.75rem 1.5rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// Action Buttons - Matching leaveRequest.vue
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Outline Action Buttons (Edit/Delete) - Matching leaveRequest.vue
.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;

  &.edit-btn {
    border: 2px solid #10b981 !important;
    color: #10b981 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);

    &:hover:not(:disabled) {
      background: #f0fdf4 !important;
      border-color: #059669 !important;
      color: #059669 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    }
  }

  &.delete-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);

    &:hover:not(:disabled) {
      background: #fef2f2 !important;
      border-color: #dc2626 !important;
      color: #dc2626 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }

  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }

  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

// Page Size Wrapper
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }

  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;

    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }

    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }

    // Close dropdown after click
    &:active {
      background-color: #e5e7eb !important;
    }

    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }

  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
<style scoped>
.border-dashed {
  border: 1px solid var(--primary);
  border-style: dashed;
}
.image {
  float: left;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  border: 1px solid #ebebeb;
  margin: 5px;
}
.card {
  transition: 0.4s;
}
.card:hover {
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.2);
}
.file-type {
  background-color: #e62e2d;
  font-family: "Helvetica", sans-serif;
  color: white;
  border-radius: 10px;
  margin-right: 15px;
  height: 80px;
  width: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}
</style>
